import React, { useState, useEffect } from 'react'
import { 
  Plus, 
  X, 
  Search,
  Package
} from 'lucide-react'

interface Servico {
  id: string
  nome: string
  descricao?: string
  valor_padrao: number
  categoria?: string
  ativo: boolean
  servico_avulso?: boolean
}

interface ServicoSelecionado {
  servico_id: string
  valor_customizado?: number
}

interface ServiceDropdownSelectorProps {
  servicosSelecionados: ServicoSelecionado[]
  onChange: (servicos: ServicoSelecionado[]) => void
  servicosDisponiveis?: Servico[]
}

const ServiceDropdownSelector: React.FC<ServiceDropdownSelectorProps> = ({
  servicosSelecionados,
  onChange,
  servicosDisponiveis
}) => {
  const [servicos, setServicos] = useState<Servico[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedServiceId, setSelectedServiceId] = useState('')
  const [searchTerm, setSearchTerm] = useState('')

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0)
  }

  useEffect(() => {
    if (servicosDisponiveis) {
      setServicos(servicosDisponiveis.filter(servico => servico.ativo))
      setLoading(false)
    } else {
      fetchServicos()
    }
  }, [servicosDisponiveis])

  const fetchServicos = async () => {
    setLoading(true)
    try {
      const response = await fetch('/api/servicos')
      const data = await response.json()
      
      if (data.data) {
        setServicos(data.data.filter((servico: Servico) => servico.ativo))
      }
    } catch (error) {
      console.error('Erro ao carregar serviços:', error)
    } finally {
      setLoading(false)
    }
  }

  // Filtrar serviços disponíveis (ativos e não selecionados)
  const servicosParaSelecao = servicos.filter(servico => 
    !servicosSelecionados.some(sel => sel.servico_id === servico.id) &&
    (searchTerm === '' || 
     servico.nome.toLowerCase().includes(searchTerm.toLowerCase()) ||
     (servico.categoria?.toLowerCase() || '').includes(searchTerm.toLowerCase())
    )
  )

  const adicionarServico = () => {
    if (!selectedServiceId) return

    const novoServico: ServicoSelecionado = {
      servico_id: selectedServiceId
    }

    onChange([...servicosSelecionados, novoServico])
    setSelectedServiceId('')
    setSearchTerm('')
  }

  const removerServico = (servicoId: string) => {
    onChange(servicosSelecionados.filter(s => s.servico_id !== servicoId))
  }

  const calcularTotal = () => {
    return servicosSelecionados.reduce((total, servicoSel) => {
      const servico = servicos.find(s => s.id === servicoSel.servico_id)
      return total + (servico?.valor_padrao || 0)
    }, 0)
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-orange-600"></div>
        <span className="ml-2 text-gray-600">Carregando serviços...</span>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium text-gray-900">Serviços do Plano</h3>
        {servicosSelecionados.length > 0 && (
          <div className="text-sm text-gray-600">
            {servicosSelecionados.length} serviço{servicosSelecionados.length !== 1 ? 's' : ''} selecionado{servicosSelecionados.length !== 1 ? 's' : ''}
          </div>
        )}
      </div>

      {/* Seleção de Serviços */}
      <div className="flex gap-3">
        <div className="flex-1">
          <select
            value={selectedServiceId}
            onChange={(e) => setSelectedServiceId(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
          >
            <option value="">Selecionar Serviço</option>
            {servicosParaSelecao.map(servico => (
              <option key={servico.id} value={servico.id}>
                {servico.nome} - {formatCurrency(servico.valor_padrao)}
                {servico.servico_avulso && ' (Serviço Avulso)'}
              </option>
            ))}
          </select>
        </div>
        <button
          type="button"
          onClick={adicionarServico}
          disabled={!selectedServiceId}
          className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center space-x-2"
        >
          <Plus className="w-4 h-4" />
          <span>Adicionar</span>
        </button>
      </div>

      {/* Busca rápida */}
      {servicosParaSelecao.length > 5 && (
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <input
            type="text"
            placeholder="Buscar serviços..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
          />
        </div>
      )}

      {/* Lista de Serviços Selecionados */}
      {servicosSelecionados.length > 0 ? (
        <div className="space-y-3">
          <h4 className="text-sm font-medium text-gray-700">Serviços Selecionados:</h4>
          <div className="border border-gray-200 rounded-lg divide-y divide-gray-200">
            {servicosSelecionados.map(servicoSel => {
              const servico = servicos.find(s => s.id === servicoSel.servico_id)
              if (!servico) return null

              return (
                <div key={servicoSel.servico_id} className="flex items-center justify-between p-3 hover:bg-gray-50">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2">
                      <h5 className="text-sm font-medium text-gray-900">{servico.nome}</h5>
                      {servico.servico_avulso && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          Serviço Avulso
                        </span>
                      )}
                    </div>
                    {servico.categoria && (
                      <p className="text-xs text-gray-500">{servico.categoria}</p>
                    )}
                  </div>
                  <div className="flex items-center space-x-3">
                    <span className="text-sm font-medium text-gray-900">
                      {formatCurrency(servico.valor_padrao)}
                    </span>
                    <button
                      type="button"
                      onClick={() => removerServico(servico.id)}
                      className="text-red-600 hover:text-red-800 p-1"
                      title="Remover serviço"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )
            })}
          </div>

          {/* Total */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-blue-900">Total dos Serviços:</span>
              <span className="text-lg font-bold text-blue-900">
                {formatCurrency(calcularTotal())}
              </span>
            </div>
          </div>
        </div>
      ) : (
        <div className="text-center py-8 text-gray-500 border-2 border-dashed border-gray-300 rounded-lg">
          <Package className="mx-auto h-8 w-8 text-gray-400 mb-2" />
          <p className="text-sm">Nenhum serviço selecionado</p>
          <p className="text-xs text-gray-400">
            Selecione os serviços que farão parte deste plano
          </p>
        </div>
      )}
    </div>
  )
}

export default ServiceDropdownSelector
